import * as React from 'react';

import InspectLinkedText from '../../components/InspectLinkedText';

// Bracket-tag prefix convention from the assistant memory
// aggregates (`[todo]`, `[done]`, etc.). Match the *first* bracketed
// token only — `[meta][todo]` highlights `[meta]` and leaves the rest
// of the body untouched. Non-greedy `[^\]]*` so a body like
// `[todo] foo [done] bar` doesn't swallow the line.
const TAG_PREFIX_REGEX = /^\[[^\]]*\]/;

interface IMemoryBodyTextProps {
  body: string;
}

/** A memory's body: its bracket tag prefix flagged, every id in the rest linked to the object inspector. */
export const MemoryBodyText = ({ body }: IMemoryBodyTextProps) => {
  const match = body.match(TAG_PREFIX_REGEX);
  if (!match) {
    return <InspectLinkedText text={body} />;
  }
  return (
    <>
      <span className="text-fg-success-primary font-bold">{match[0]}</span>
      <InspectLinkedText text={body.slice(match[0].length)} />
    </>
  );
};

interface IMemoryBodyCellProps {
  body: string;
  expanded: boolean;
  onToggle: () => void;
}

/**
 * The memory table's body column: two clamped lines that open into the whole
 * body. The text is deliberately not the button it used to be — it carries
 * inspector links now, and an anchor inside a button is invalid HTML with two
 * click targets fighting over the same pixels. The chevron is the keyboard
 * path; clicking the text still toggles, except on a link.
 */
export const MemoryBodyCell = ({ body, expanded, onToggle }: IMemoryBodyCellProps) => {
  const toggleUnlessLink = (event: React.MouseEvent<HTMLElement>) => {
    if (event.target instanceof Element && event.target.closest('a') !== null) {
      return;
    }
    onToggle();
  };
  return (
    <div className="flex items-start gap-1 font-mono text-[11px] leading-snug">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={expanded}
        aria-label={expanded ? 'Collapse body' : 'Expand body'}
        title={expanded ? 'Collapse' : 'Expand'}
        className="text-fg-quaternary hover:text-fg-primary shrink-0 cursor-pointer"
      >
        {expanded ? '▾' : '▸'}
      </button>
      {/* Not focusable on purpose: the chevron is the keyboard path, and a
          second tab stop per row would bury the links inside the body. */}
      <div onClick={toggleUnlessLink} className="hover:text-fg-primary min-w-0 flex-1 cursor-pointer">
        {expanded ? (
          <pre className="bg-bg-secondary text-fg-primary max-h-64 overflow-auto whitespace-pre-wrap break-words p-2">
            <MemoryBodyText body={body} />
          </pre>
        ) : (
          <span className="line-clamp-2 break-words">
            <MemoryBodyText body={body} />
          </span>
        )}
      </div>
    </div>
  );
};
